import React, { useState, useEffect } from "react";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FolderOpen, X } from "lucide-react";
import { Button } from "react-bootstrap";
import localization from "services/localization";
import { getResourceType } from "services/utils";
import ResourceManagerModal from "views/Components/ResourceLibrary/ResourceManagerModal";
import ClipLoader from "react-spinners/ClipLoader";
import SpecificAPI from "services/SpecificApi";
import { useUserStore } from "context/useUserStore";

export default function ResourceComponent({
  onSelectResources,
  selectedResources,
  resourceType,
}) {
  const [resources, setResources] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const { appUser, userData } = useUserStore();

  useEffect(() => {
    if (!appUser.id) return;
    getResources(appUser?.id);
  }, [appUser?.id]);

  const getResources = async (userId) => {
    try {
      setFetchLoading(true);

      const paramsForUser = { user_id: userId };
      const paramsForPublic = { visibility_scope: "public" };

      // Add to_rag param only if resourceType is defined
      if (resourceType === "rag") {
        paramsForUser.to_rag = true;
        paramsForPublic.to_rag = true;
      } else if (resourceType === "nonRag") {
        paramsForUser.to_rag = false;
        paramsForPublic.to_rag = false;
      }

      // const [userResources, publicResources] = await Promise.all([
      //   SpecificAPI.getAllResources(
      //     paramsForUser,
      //     userData?.selected_school_id,
      //   ),
      //   SpecificAPI.getAllResources(
      //     paramsForPublic,
      //     userData?.selected_school_id,
      //   ),
      // ]);

      const resources = await SpecificAPI.getAllResources(
        { enabled: true },
        userData?.selected_school_id,
      );

      // const merged = [...userResources, ...publicResources];
      // const uniqueResources = Array.from(
      //   new Map(merged.map((r) => [r.id, r])).values(),
      // );

      setResources(resources);
    } catch (error) {
      console.error("Failed to fetch resources", error);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleResourceModalClose = () => {
    setShowResourceModal(false);
  };

  return (
    <>
      <Accordion
        defaultExpanded={false}
        variant="outlined"
        disableGutters
        className="themed-accordion"
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className="themed-accordion-summary"
        >
          <div className="flex items-center justify-between w-full pr-2">
            <div className="flex items-center gap-2">
              <FolderOpen className="text-blue-500" size={18} />
              <span className="accordion-title">
                {localization.resources || "Resources"}
              </span>

              {selectedResources.length > 0 && (
                <span className="accordion-count">
                  {selectedResources.length}{" "}
                  {localization.resource_selected_count ||
                    "resource(s) selected"}
                </span>
              )}
            </div>

            <Button
              size="sm"
              className="action-btn-outline primary text-xs font-medium px-3 py-1 rounded-pill"
              onClick={(e) => {
                e.stopPropagation();
                setShowResourceModal(true);
              }}
              disabled={fetchLoading}
            >
              {fetchLoading
                ? localization.loading || "Loading..."
                : localization.browse_library || "Browse Library"}
            </Button>
          </div>
        </AccordionSummary>

        <AccordionDetails>
          {fetchLoading ? (
            <div className="flex items-center justify-center py-6">
              <ClipLoader size={28} color="var(--primary-icon)" />
            </div>
          ) : selectedResources.length ? (
            <div className="resource-list">
              {selectedResources.map((id, index) => {
                const resource = resources.find((r) => r.id === id);
                if (!resource) return null;

                return (
                  <div key={id} className="selected-resource-card">
                    <div className="min-w-0">
                      <p className="resource-name">
                        {resource.originalName || "Unknown"}
                      </p>
                      <p className="resource-type">
                        {getResourceType(resource)}
                      </p>
                    </div>

                    <button
                      onClick={() => {
                        if (!selectedResources) return;
                        const updatedResources = [...selectedResources];
                        updatedResources.splice(index, 1);
                        onSelectResources(updatedResources);
                      }}
                      className="remove-resource-btn"
                    >
                      <X size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-gray-500 italic">
              {localization.no_resources_selected ||
                "No resources selected yet"}
            </div>
          )}
        </AccordionDetails>
      </Accordion>
      <ResourceManagerModal
        show={showResourceModal}
        onClose={handleResourceModalClose}
        onSelectResources={(ids) => {
          onSelectResources(ids);
          setShowResourceModal(false);
        }}
        selectedResources={selectedResources}
        modalResources={
          resources.filter((r) => selectedResources?.includes(r.id)) || []
        }
        resources={resources}
        setResources={setResources}
        loading={fetchLoading}
        setLoading={setFetchLoading}
        resourceType={resourceType}
      />
    </>
  );
}
